import { Button, CircularLoader, Layout, Modal, useToast } from "@introist/react-foundation/v2";
import { useCallback, useEffect, useState } from "react";
import { ConnectDataSourceInput, api } from "services/rpc/RpcProvider";
import { getDataSourceSpec } from "../datasources";

type Props = {
  open: boolean;
  onClose: VoidFunction;
  dataSourceId?: string;
};

export const DataSourceCredentialUpdateModal = ({ open, onClose, dataSourceId }: Props) => {
  const toast = useToast();
  const { data: dataSource } = api.employees.dataSources.find.useQuery(
    { dataSourceId: dataSourceId ?? "" },
    { enabled: !!dataSourceId && open }
  );
  const { mutateAsync: updateCredentials } =
    api.employees.dataSources.updateCredentials.useMutation();

  const [values, setValues] = useState<Partial<ConnectDataSourceInput>>({});

  useEffect(() => {
    if (dataSource) {
      setValues({ sourceType: dataSource.sourceType, name: dataSource.name, credentials: {} });
    }
  }, [dataSource]);

  const handleSave = useCallback(async () => {
    if (!dataSourceId || !values.credentials) return;
    await updateCredentials({ dataSourceId, credentials: values.credentials })
      .then(() => {
        toast.success("Credentials updated");
        onClose();
      })
      .catch(err => {
        toast.error(err?.message ?? "Failed to update credentials");
      });
  }, [dataSourceId, values.credentials, updateCredentials, toast, onClose]);

  const spec = dataSource ? (() => {
    try { return getDataSourceSpec(dataSource.sourceType); } catch { return null; }
  })() : null;

  return (
    <Modal open={open} onClose={onClose} title="Update credentials">
      {open && (!dataSource || !spec) && <CircularLoader fillParent colorVariant="primary" />}
      {open && dataSource && spec && (
        <Layout.Group vertical gap="xxLarge">
          <spec.ConnectForm
            values={values}
            onChange={next => setValues(current => ({ ...current, ...next, name: current.name }))}
          />
          <Layout.Group justifyContent="flex-end" gap="large">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button onClickWithLoading={handleSave}>Update credentials</Button>
          </Layout.Group>
        </Layout.Group>
      )}
    </Modal>
  );
};
